import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Tooltip from '@material-ui/core/Tooltip';
import Box from '@material-ui/core/Box';
import Switch from "@material-ui/core/Switch";
import { withStyles } from '@material-ui/core/styles';
import { green, red } from '@material-ui/core/colors';
import Skeleton from '@material-ui/lab/Skeleton';
import Collapsible from "../../components/utils/Collapsible";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip as RechartsTooltip,
} from 'recharts';
import DefaultTooltipContent from 'recharts/lib/component/DefaultTooltipContent';

import Scenario from '../../api/Scenario';
import Header from '../../components/utils/Header';
import { unitFormatter, formatPercent, formatUnit } from '../../utils';
import FilterListView from '../../components/FilterListView';
import { withRouter } from 'react-router-dom';
import { InfoIcon } from "../../theme/AthenaIcons";
import ScenarioSelect from '../scenarios/ScenarioSelect';
import BaseScenarioDashboardView from '../BaseScenarioDashboardView';
import DataFrameDownload from '../../components/utils/DataFrameDownload';


const styles = theme => ({
  chartContainer: {
    overflow: 'hidden',
    padding: theme.spacing(2),
    '& .recharts-legend-item': {
      cursor: 'pointer',
    },
  },
  paper: {
    maxHeight: '100%',
    overflow: 'auto',
  },
  increase: {
    color: green[600],
  },
  decrease: {
    color: red[600],
  },
  boldColumn: {
    fontWeight: theme.typography.fontWeightBold,
  },
});

@withStyles(styles)
@withRouter
@observer
class RawAndPacksCommitmentView extends BaseScenarioDashboardView {
  static apiSubPath = '/raw_and_packs_commitment/';

  @observable matCod = null;
  @observable scenario1 = null;
  @observable scenario2 = null;
  @observable showAllMatCod = true;
  @observable disabledSeries = [
    'delta_order_athena_vs_extrapolated_scenario_1', 'delta_order_athena_vs_extrapolated_scenario_2',
  ];
  @observable anchorInfoTooltip = null;

  _setData(data) {
    this.matCod = data.mat_cod && data.mat_cod.mat_cod ? data.mat_cod.mat_cod : data.mat_cod;
    this.scenario1 = data.scenario1;
    this.scenario2 = data.scenario2;
  }

  _getQueryParams() {
    return {
      scenario1: this.scenario1,
      scenario2: this.scenario2,
      mat_cod: this.matCod,
      showAllMatCod: this.showAllMatCod
    };
  }

  onScenario1Change = this.genericChangeHandler('scenario1');
  onScenario2Change = this.genericChangeHandler('scenario2');
  onShowAllMatCodChange = this.genericToggleHandler('showAllMatCod');
  onMatCodChange = this.genericChangeValueHandler('matCod');

  get controls() {
    if (!this.initialized) {
      return <Skeleton variant="rect" width={650} height={55} />;
    }
    console.log(this.showAllMatCod, 11111111);
    return (
      <Grid container spacing={2} justify="flex-end" alignItems="center">
        <Grid item>
          <ScenarioSelect
            scenarioId={this.scenario1}
            choices={this.data.scenario1_choices}
            onScenarioChange={this.onScenario1Change}
          />
        </Grid>
        <Grid item>
          <ScenarioSelect
            scenarioId={this.scenario2}
            choices={this.data.scenario2_choices}
            onScenarioChange={this.onScenario2Change}
          />
        </Grid>
        <Grid item>
          {/*{this.renderAutocomplete(*/}
          {/*  'MAT_COD', this.data.mat_cod_choices, this.matCod,*/}
          {/*  this.onMatCodChange,*/}
          {/*)}*/}
          <Typography noWrap>
            All RP SKUs
            <Switch
                checked={!this.showAllMatCod}
                onChange={this.onShowAllMatCodChange} />
            RP Alert Mat Cod
          </Typography>
        </Grid>
        <Grid item>
          <FilterListView filterList={this.filterList} />
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl('/raw_and_packs_commitment/?'+ this.getQueryString())}
            permission="athena.download_raw_packs_commitment"
          />
        </Grid>
      </Grid>
    );
  }

  get chart() {
    var data = [...this.data.graph_data];
    if (data.length === 0) {
      return 'No data found';
    }
    const formatter = unitFormatter(data[0].UOM_COD, 0);
    const barSize = 100;
    const maxBarSize = 100;
    const barGap = 30;
    const getColor = {
      min_consumption: '#26D0FE',
      order_actuals_scenario_1: '#2C488F',
      order_athena_scenario_1: '#6A8CDE',
      delta_order_athena_vs_extrapolated_scenario_1: '#C1C1C1',
      order_actuals_scenario_2: '#95AADF',
      order_athena_scenario_2: '#C2D3FF',
      delta_order_athena_vs_extrapolated_scenario_2: '#C1C1C1',
      max_consumption: '#0194BA'
    };

    const aliasName = {
      min_consumption: 'Contract Min',
      order_actuals_scenario_1: 'Actual orders Sc.1',
      order_athena_scenario_1: 'Athena orders Sc.1',
      delta_order_athena_vs_extrapolated_scenario_1: 'Athena extrapolated Sc.1',
      total_order_scenario_1: 'Total orders Sc.1',
      order_actuals_scenario_2: 'Actual orders Sc.2',
      order_athena_scenario_2: 'Athena orders Sc.2',
      delta_order_athena_vs_extrapolated_scenario_2: 'Athena extrapolated Sc.1',
      total_order_scenario_2: 'Total orders Sc.2',
      max_consumption: 'Contract Max'
    };

    const getStroke = (label) => {
      if (label === 'delta_order_athena_vs_extrapolated_scenario_1') {
        return getColor['order_actuals_scenario_1'];
      }
      if (label === 'delta_order_athena_vs_extrapolated_scenario_2') {
        return getColor['order_actuals_scenario_2'];
      }
      return null;
    };

    var dictXY = {};

    const renderCustomizedLabel = (props) => {
      const {
        x, y, width, height, value,
      } = props;
      Object.assign(dictXY, { [value.item]: { ...dictXY[value.item], [value.year]: { x, y, width, height } } });

      const deltaLine = (year) => (
        <g>
          <line strokeDasharray="4" strokeWidth="1" stroke="#2C488F"
            x1={dictXY["min_consumption"][year].x}
            x2={dictXY["max_consumption"][year].x + width}
            y1={dictXY["delta_order_athena_vs_extrapolated_scenario_1"][year].y - 0.02 * width}
            y2={dictXY["delta_order_athena_vs_extrapolated_scenario_1"][year].y - 0.02 * width}
          />
        </g>
      );

      const deltaArrow = (year, item) => {
        let deltaPercent = data.filter(x => x.year === year)[0]['gap_scenario_1_'.concat(item)];
        deltaPercent = deltaPercent < 0 ? formatPercent(deltaPercent, 1) : '+'.concat(formatPercent(deltaPercent, 1));
        return (dictXY[item][year].height === 0 ? null : (
          <g>
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5"
                markerWidth="7" markerHeight="7"
                orient="auto-start-reverse" fill="black">
                <path d="M 0 0 L 10 5 L 0 10 z" />
              </marker>
            </defs>
            <line strokeWidth="1" stroke="black"
              x1={dictXY[item][year].x + width / 2}
              x2={dictXY[item][year].x + width / 2}
              y1={dictXY['delta_order_athena_vs_extrapolated_scenario_1'][year].y}
              y2={item === 'order_athena_scenario_2' ? dictXY['delta_order_athena_vs_extrapolated_scenario_2'][year].y : dictXY[item][year].y}
              markerEnd="url(#arrow)"
            />
            <text x={dictXY[item][year].x + width / 2 - 0.1 * width}
              y={Math.min(dictXY['delta_order_athena_vs_extrapolated_scenario_1'][year].y, item === 'order_athena_scenario_2' ? dictXY['delta_order_athena_vs_extrapolated_scenario_2'][year].y : dictXY[item][year].y) - 0.1 * width}
              fontSize={width / 6}
            >
              {deltaPercent}
            </text>
          </g>
        ));
      };
      return (
        <g>
          {value.item === 'max_consumption' ? deltaLine(value.year) : null}
          {value.item === 'max_consumption' ? deltaArrow(value.year, 'min_consumption') : null}
          {value.item === 'max_consumption' ? deltaArrow(value.year, 'order_athena_scenario_2') : null}
          {value.item === 'max_consumption' ? deltaArrow(value.year, 'max_consumption') : null}
        </g>
      );
    };

    const customRechartsTooltip = ({ payload, label }) => {

      const isNormalSize = {
        min_consumption: true,
        total_order_scenario_1: true,
        order_actuals_scenario_1: false,
        order_athena_scenario_1: false,
        delta_order_athena_vs_extrapolated_scenario_1: false,
        total_order_scenario_2: true,
        order_actuals_scenario_2: false,
        order_athena_scenario_2: false,
        delta_order_athena_vs_extrapolated_scenario_2: false,
        max_consumption: true
      };

      const formatTextSize = (value, name, item, index) => {
        return (
          [<Box fontSize={isNormalSize[name] ? "1em" : ".7em"} ml={isNormalSize[name] ? "0" : "10%"} >
            {aliasName[name]} : {formatter(value)}
          </Box>, <Box />]);
      };

      var extraPayload = {
        fillOpacity: this.getOpacity('order_actuals_scenario_1'),
        fill: getColor['order_actuals_scenario_1'],
        dataKey: 'total_order_scenario_1',
        unit: undefined,
        formatter: undefined,
        name: 'total_order_scenario_1',
        color: getColor['order_actuals_scenario_1'],
        value: label ? data.filter((x) => x.year === label)[0].total_order_scenario_1 : label,
        type: undefined,
      };
      var insertAtIndex = payload.findIndex((x) => x.name.endsWith('scenario_1'));
      if (insertAtIndex !== -1) { payload.splice(insertAtIndex, 0, extraPayload) };

      if (payload.filter(x => x.name.endsWith('scenario_2')).length > 0) {
        extraPayload = {
          fillOpacity: this.getOpacity('order_actuals_scenario_2'),
          fill: getColor['order_actuals_scenario_2'],
          dataKey: 'total_order_scenario_2',
          unit: undefined,
          formatter: undefined,
          name: 'total_order_scenario_2',
          color: getColor['order_actuals_scenario_2'],
          value: label ? data.filter((x) => x.year === label)[0].total_order_scenario_2 : label,
          type: undefined,
        };
        insertAtIndex = payload.findIndex((x) => x.name.endsWith('scenario_2'));
        if (insertAtIndex !== -1) { payload.splice(insertAtIndex, 0, extraPayload) };
      }
      return <DefaultTooltipContent label={label} payload={payload} formatter={formatTextSize} labelStyle={{ fontSize: '1.17em', fontWeight: 'bold' }} />;
    };

    const bars = ['min_consumption', 'order_actuals_scenario_1', 'order_athena_scenario_1', 'delta_order_athena_vs_extrapolated_scenario_1',
      'order_actuals_scenario_2', 'order_athena_scenario_2', 'delta_order_athena_vs_extrapolated_scenario_2', 'max_consumption'];
    const stackId = (bar) => (bar.endsWith('scenario_1') ? 'sc1' : (bar.endsWith('scenario_2') ? 'sc2' : null));

    return (
      <ResponsiveContainer aspect={21 / 9}>
        <BarChart data={data} margin={{ top: 50, right: 20, bottom: 20, left: 20 }} barGap={barGap}>
          <CartesianGrid stroke='#f5f5f5' />
          <XAxis dataKey="year" />
          <YAxis orientation="left" tickFormatter={formatter} tick={{ fontSize: 12 }} />
          <RechartsTooltip content={customRechartsTooltip} />
          <Legend onClick={this.onLegendClick} formatter={this.legendFormatter} />
          {bars.map((bar) => (
            this.getBar(
              bar,
              {
                key: bar, stackId: stackId(bar), fill: getColor[bar], strokeDasharray: '5 5', stroke: getStroke(bar),
                barSize, maxBarSize,
                children: (
                  <LabelList
                    dataKey={(row) => ({ year: row.year, item: bar, row: row })}
                    content={renderCustomizedLabel}
                  />
                )
              })
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  formatValue(column, value, unit) {
    if (value === null) {
      return '—';
    }
    if (column === "Contract Max" || column === "Contract Min" || column === "Orders Sc.1" || column === "Orders Sc.2" || column === 'Gap Sc.1 vs. Sc.2') {
      return formatUnit(value, unit, 0);
    }
    if (column === "% actuals Sc.1" || column === "% actuals Sc.2" || column === "% Gap Sc.1 vs. Sc.2" || column === "Alert threshold") {
      return formatPercent(value, 1);
    }
    return value;
  };


  colorValue(column, row) {
    if (column === 'Contract Min') {
      return row[column] <= row["Orders Sc.1"] ? 'green' : 'red';
    }
    if (column === 'Contract Max') {
      return row[column] >= row["Orders Sc.1"] ? 'green' : 'red';
    }
    if (column === '% Gap Sc.1 vs. Sc.2') {
      return (row[column] <= row["Alert threshold"] && -row["Alert threshold"] <= row[column]) ? 'green' : 'red';
    }
    return;
  };


  table(data, title = '', compare = false) {
    const { classes } = this.props;
    if (data.length < 1) {
      return <h1>There is no data to show.</h1>
    }
    const displayedColumns = Object.keys(data[0]);
    return (
      <Paper className={classes.paper}>
        <Collapsible title={title}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {displayedColumns.map(key => (
                  <TableCell key={key} align={key === 'index' ? 'left' : 'right'}>
                    {key === 'index' ? null : key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={index}>
                  {displayedColumns.map(key =>
                    <TableCell
                      key={key}
                      align={key === 'index' ? 'left' : 'right'}
                      className={key.indexOf('FY') ? '' : classes.boldColumn}
                      style={{ color: this.colorValue(key, row) }}>
                      {key === 'index' ? row[key] : this.formatValue(key, row[key], row["UOM_COD"])}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapsible>
      </Paper>
    );
  }

  get tablesPerYear() {
    return this.data.table_data_per_year.map(dataPerYear =>
      <Grid item key={dataPerYear.year}>
        {this.table(dataPerYear.table_data, dataPerYear.year)}
        <br />
      </Grid>
    );
  }

  infoTooltip = () => {
    const handleClick = event => {
      this.anchorInfoTooltip = event.currentTarget;
    };
    const handleClose = () => {
      this.anchorInfoTooltip = null;
    };
    const open = Boolean(this.anchorInfoTooltip);
    const id = open ? 'simple-popover' : undefined;

    return (
      <>
        <Tooltip title="Info">
          <IconButton onClick={handleClick}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
        <Popover
          id={id}
          open={open}
          anchorEl={this.anchorInfoTooltip}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          style={{ 'width': '60%' }}
        >
          <Box m={2}>
            <Typography variant="h6">
              How Athena estimates R&P orders
            </Typography>
            <Typography variant="body1">
              To estimate the total R&P orders over a year, Athena uses: <br />
            - R&P purchasing actuals up-to-date, <br />
            - Athena's forecast over the scenario period. <br />
              <br />
            If there is any time overlap between actuals and scenario: Athena uses actuals values during the overlap<br />
            If there is a time gap between actuals and scenario: Athena does not bridge this gap<br />
            If scenario period does not cover a full year: Athena extrapolate its full-year results at pro rata of the scenario time horizon (see dashed bars)<br />
            </Typography>
          </Box>
        </Popover>
      </>
    );
  };

  renderContent() {
    if (!this.ready) {
      return <Grid item><Skeleton variant="rect" height={550} /></Grid>;
    }
    const { classes } = this.props;
    return (
      <>
        <Grid item>
          <Paper className={classes.chartContainer}>
            <Typography variant="h6">
              Raw & Packs consumption forecasts
            </Typography>
            {this.data.mat_cod.mat_cod} – {this.data.mat_cod.mat_dsc}
            {this.infoTooltip()}
            {this.chart}
          </Paper>
        </Grid>
        <Grid item>
          {this.tablesPerYear}
        </Grid>
      </>
    );
  }

  render() {
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title="Raw & Packs Commitment" />
          </Grid>
          <Grid item>
            {this.controls}
          </Grid>
        </Grid>
        {this.renderContent()}
      </Grid>
    );
  }

}

export default RawAndPacksCommitmentView;
